export interface MatomoInstanceFlags {
	useDisableCookies?: boolean;
	useSetAPIUrl?: boolean;
	useEnableCrossDomainLinking?: boolean;
	useSetCookiePath?: boolean;
	useSetCookieDomain?: boolean;
	useSetDomains?: boolean;
	useSetUserId?: boolean;
	useEnableLinkTracking?: boolean;
}

export interface MatomoTrackerSpecification extends MatomoInstanceFlags {
	siteId: string;
}

// F needs to extend Function here
// eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-explicit-any
type ArgumentTypes<F extends Function> = F extends (...args: infer A) => any ? A : never;
type InterceptedArgs<T extends keyof PiwikInstanceApi> = [T, ArgumentTypes<PiwikInstanceApi[T]>];

// This is heavily inspired by:
// https://github.com/Amsterdam/matomo-tracker/blob/master/packages/js/src/MatomoTracker.ts
// https://github.com/Arnaud73/ngx-matomo/blob/master/projects/ngx-matomo/src/lib/matomo-tracker.service.ts
// It's a merge between them
export class MatomoTracker {
	public readonly siteId: string;
	public readonly flags: MatomoInstanceFlags;
	private storedTrackings: InterceptedArgs<keyof PiwikInstanceApi>[] = [];
	private connectedPiwikInstance: PiwikInstanceApi | null = null;
	private wasKilled = false;

	/* @internal */
	public static create(spec: MatomoTrackerSpecification): MatomoTracker {
		return new MatomoTracker(spec);
	}

	private constructor({ siteId, ...rest }: MatomoTrackerSpecification) {
		this.siteId = siteId;

		this.flags = Object.freeze({
			...rest,
		});
	}

	/**
	 * Disables all tracking for the Matomo instance.
	 */
	public kill() {
		this.wasKilled = true;
		this.connectedPiwikInstance = null;
		this.storedTrackings = [];
	}

	/**
	 * A side effect that occurs when attaching a loaded Piwik instance is that
	 * each stored tracking event up to this point will be dispatched.
	 */
	public attachPiwikInstance(piwikInstance: PiwikInstanceApi): void {
		this.connectedPiwikInstance = piwikInstance;

		// Dispatches all stored tracking actions. Empties the array in the process
		while (this.storedTrackings.length > 0) {
			const storedTracking = this.storedTrackings.shift();
			if (!storedTracking) break;
			this.dispatchAction(storedTracking);
		}
	}

	/**
	 * Custom variable statistics are reported in Matomo under Visitors > custom variables.
	 *
	 * https://developer.matomo.org/guides/tracking-javascript-guide#custom-variables
	 *
	 * @param {number} params.index - The number from 1 to 5 where this custom variable name is stored
	 * @param {string} params.category - The name of the variable, for example: `Gender`, `VisitorType`
	 * @param {string} params.value - The value, for example: `Male`, `Female` or `new`, `engaged`, `customer`
	 * @param {string} params.scope - Scope of the custom variable, `visit` means the custom variable applies to the current visit
	 */
	public setCustomVariable(params: { index: 1 | 2 | 3 | 4 | 5; category: string; value: string; scope: string }): void {
		this.isAlive();

		const { index, category, scope, value } = params;

		this.interceptAction('setCustomVariable', [index, category, value, scope]);
	}

	/**
	 * Custom dimension statistics are reported in Matomo under Visitors.
	 *
	 * https://developer.matomo.org/guides/tracking-javascript-guide#custom-dimensions
	 *
	 * @param {number} params.id - The custom dimension id
	 * @param {string} params.value - The value, for example: `Male`, `Female` or `new`, `engaged`, `customer`
	 */
	public setCustomDimension(params: { id: number; value: string }): void {
		this.isAlive();

		const { id, value } = params;

		this.interceptAction('setCustomDimension', [id, value]);
	}

	/**
	 * Tracks events
	 *
	 * https://matomo.org/docs/event-tracking/#tracking-events
	 *
	 * @param {string} params.category - Category of the event
	 * @param {string} params.action - Action of the event
	 * @param {string=} params.name - Optional name of the event
	 * @param {number=} params.value - Optional value for the event
	 */
	public trackEvent(params: { category: string; action: string; name?: string; value?: number }): void {
		this.isAlive();

		const { category, action, name, value } = params;

		if (category && action) {
			this.track({
				data: ['trackEvent', [category, action, name, value]],
			});
		} else {
			throw new Error('Category and action are required');
		}
	}

	/**
	 * Tracks page views
	 *
	 * https://developer.matomo.org/guides/spa-tracking#tracking-a-new-page-view
	 *
	 * @param {string=} params.customTitle - Custom title for the page
	 */
	public trackPageView(params: { customTitle: string; documentTitle?: string; href?: string }): void {
		this.isAlive();

		const { customTitle, documentTitle, href } = params;

		this.track({
			documentTitle,
			href,
			data: ['trackPageView', [customTitle]],
		});
	}

	/** Sends the tracked page/view/search to Matomo */
	private track(params: { data: InterceptedArgs<keyof PiwikInstanceApi>; documentTitle?: string; href?: string | Location }): void {
		this.isAlive();

		const { data = [], documentTitle = window.document.title, href = window.location.href } = params;

		if (data.length === 0) return;

		this.interceptAction('setCustomUrl', [href]);
		this.interceptAction('setDocumentTitle', [documentTitle]);
		this.interceptAction(...data);
	}

	private interceptAction<T extends keyof PiwikInstanceApi>(...args: InterceptedArgs<T>): void {
		this.isAlive();

		// This interceptor exists to not lose events when piwik is loading asynchronously. During piwik load it'll queue up all requested actions instead of dispatching them
		if (this.connectedPiwikInstance == null) {
			this.storedTrackings.push(args);
		} else {
			this.dispatchAction(args);
		}
	}

	private dispatchAction<T extends keyof PiwikInstanceApi>(args: InterceptedArgs<T>): void {
		this.isAlive();

		if (!this.connectedPiwikInstance) throw new Error(`no piwik instance has been linked to this MatomoTracker`);
		if (!this.connectedPiwikInstance[args[0]]) throw new Error(`${args[0]} is not included in the piwik's public api`);

		// This is extremely hard to type correctly
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		this.connectedPiwikInstance[args[0]](...args[1]);
	}

	private isAlive() {
		if (this.wasKilled) throw new Error('Matomo tracker is disabled, tracking should not be called.');
	}
}
